import React, { useState, useRef } from 'react';
import './App.css';

// Helper function to format bytes into a readable string
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

function App() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
  const [optimizedImageUrl, setOptimizedImageUrl] = useState<string | null>(null);
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [optimizedSize, setOptimizedSize] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outputFormat, setOutputFormat] = useState('webp');
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
        setError('画像ファイル以外はアップロードできません。');
        return;
    }
    setSelectedFile(file);
    setOptimizedImageUrl(null);
    setOptimizedSize(null);
    setError(null);
    setOriginalSize(file.size);

    const reader = new FileReader();
    reader.onloadend = () => {
      setOriginalImageUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleOptimizeClick = async () => {
    if (!selectedFile) {
      setError('Please select an image file first.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setOptimizedImageUrl(null);
    setOptimizedSize(null);

    const formData = new FormData();
    formData.append('image', selectedFile);
    formData.append('format', outputFormat);

    try {
      const response = await fetch('https://us-central1-image-compression-497a4.cloudfunctions.net/api/optimize', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to optimize image.');
      }

      const imageBlob = await response.blob();
      setOptimizedSize(imageBlob.size);
      const downloadUrl = URL.createObjectURL(imageBlob);
      setOptimizedImageUrl(downloadUrl);

    } catch (err: any) {
      setError(err.message || 'An unknown error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Drag and Drop Handlers ---
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>画像圧縮サービス</h1>
        <p>画像をアップロードして、ファイルサイズを圧縮します。</p>
      </header>
      <main className="content">
        <div 
          className={`upload-section ${isDragging ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input 
            type="file" 
            accept="image/png, image/jpeg, image/gif"
            onChange={handleFileChange} 
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          <p>ここにファイルをドラッグ＆ドロップ</p>
          <p>または</p>
          <button className="btn btn-upload" onClick={() => fileInputRef.current?.click()}>
            画像を選択
          </button>
          <p className="upload-note">※ 256MB未満のファイルのみアップロード可能です。</p>
        </div>

        {selectedFile && (
          <div className="optimize-section">
            <div className="format-selector">
              <label htmlFor="format">出力フォーマット:</label>
              <select id="format" value={outputFormat} onChange={e => setOutputFormat(e.target.value)}>
                <option value="webp">WebP</option>
                <option value="jpeg">JPEG</option>
                <option value="png">PNG</option>
                <option value="gif">GIF</option>
              </select>
            </div>
            <button className="btn btn-optimize" onClick={handleOptimizeClick} disabled={isLoading}>
              {isLoading ? '圧縮中...' : '圧縮する'}
            </button>
          </div>
        )}

        {error && <p className="error-message">エラー: {error}</p>}

        <div className="results-section">
          {originalImageUrl && (
            <div className="image-container">
              <h2>元画像</h2>
              <img src={originalImageUrl} alt="Original" />
              {originalSize && <p className="file-size">ファイルサイズ: {formatBytes(originalSize)}</p>}
            </div>
          )}
          {optimizedImageUrl && (
            <div className="image-container">
              <h2>圧縮画像 ({outputFormat.toUpperCase()})</h2>
              <img src={optimizedImageUrl} alt="Optimized" />
              {optimizedSize && <p className="file-size">ファイルサイズ: {formatBytes(optimizedSize)}</p>}
              <a href={optimizedImageUrl} download={`optimized.${outputFormat}`} className="btn btn-download">
                ダウンロード
              </a>
            </div>
          )}
        </div>

        <section className="faq-section">
          <h2>よくある質問</h2>
          <details>
            <summary>Q1: このサービスは何ですか？</summary>
            <p>A: このサービスは、画像ファイルのサイズを小さく（圧縮）することで、ウェブサイトの表示を速くしたり、ストレージの空き容量を増やしたりするためのツールです。</p>
          </details>
          <details>
            <summary>Q2: どうして画像のサイズが小さくなるのですか？</summary>
            <p>A: 主に「非可逆圧縮」と「可逆圧縮」という2つの方法を使って、画像のデータ量を減らしています。</p>
          </details>
          <details>
            <summary>Q3: 「非可逆圧縮」と「可逆圧縮」って何ですか？</summary>
            <p>A: </p>
            <ul>
              <li>
                <strong>非可逆圧縮 (JPEG, WebPなど):</strong><br/>
                人間の目には分かりにくいごくわずかな情報を少しだけ取り除くことで、ファイルサイズを劇的に小さくします。一度圧縮すると元には戻せませんが、写真などには最適です。例えるなら「本のあらすじ」を作るようなものです。
              </li>
              <li>
                <strong>可逆圧縮 (PNG, GIFなど):</strong><br/>
                データを一つも失わずに、記録の仕方を工夫してサイズを小さくします。画質は全く変わりません。ロゴやイラストなど、画質の劣化を避けたい場合に最適です。例えるなら「衣類の圧縮袋」を使うようなものです。
              </li>
            </ul>
          </details>
        </section>
      </main>
    </div>
  );
}

export default App;
